import { useContext, useEffect, useRef, useReducer } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UpdateUserPreferences, UserPreferences } from '@reflex/shared';
import './http';
import { AuthContext } from './PluginClientContext';
import { getMyPreferences, updateMyPreferences } from './generated/user';

export function getUserPreferences(): Promise<UserPreferences> {
  return getMyPreferences();
}

export function updateUserPreferences(
  preferences: UpdateUserPreferences,
): Promise<UserPreferences> {
  return updateMyPreferences(preferences);
}

export function userPreferencesQueryKey(userId: string | undefined) {
  return ['user', userId, 'preferences'] as const;
}

export function useUserPreferencesQuery() {
  const auth = useContext(AuthContext);
  const userId = auth?.user?.id;
  return useQuery({
    meta: { topProgress: false },
    queryKey: userPreferencesQueryKey(userId),
    queryFn: getUserPreferences,
    staleTime: Infinity,
    enabled: !auth?.setupRequired && Boolean(userId),
  });
}

export function useUpdateUserPreferences() {
  const auth = useContext(AuthContext);
  const userId = auth?.user?.id;
  const queryClient = useQueryClient();
  return useMutation({
    meta: { topProgress: false },
    mutationFn: updateUserPreferences,
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey: userPreferencesQueryKey(userId) });
      queryClient.setQueryData<UserPreferences>(userPreferencesQueryKey(userId), (current) =>
        current ? { ...current, ...patch } : current,
      );
    },
    onSuccess: (preferences) => {
      queryClient.setQueryData<UserPreferences>(userPreferencesQueryKey(userId), preferences);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: userPreferencesQueryKey(userId) });
    },
  });
}

/**
 * Reconcile a local first-paint preference with its account-level value.
 * The server wins when an account value exists. Otherwise the current local
 * value seeds the account, preserving preferences chosen before sync existed.
 */
export function useSyncedUserPreference<K extends keyof UserPreferences>(
  key: K,
  value: NonNullable<UserPreferences[K]>,
  setValue: (value: NonNullable<UserPreferences[K]>) => void,
  options: { enabled?: boolean } = {},
): void {
  const auth = useContext(AuthContext);
  const userId = auth?.user?.id;
  const preferences = useUserPreferencesQuery();
  const mutation = useUpdateUserPreferences();
  const mutate = mutation.mutate;
  const hydratedRef = useRef(false);
  const identityRef = useRef(userId);
  const lastSyncedRef = useRef<UserPreferences[K]>(null);
  const pendingHydrationRef = useRef<UserPreferences[K]>(null);
  const [retryTick, forceRetry] = useReducer((n: number) => n + 1, 0);
  const enabled = options.enabled ?? true;

  useEffect(() => {
    if (identityRef.current === userId) return;
    identityRef.current = userId;
    hydratedRef.current = false;
    lastSyncedRef.current = null;
    pendingHydrationRef.current = null;
  }, [userId]);

  useEffect(() => {
    if (!enabled || hydratedRef.current || !preferences.isSuccess || !userId) return;
    hydratedRef.current = true;
    const stored = preferences.data[key];
    if (stored === null) {
      lastSyncedRef.current = value;
      mutate({ [key]: value } as UpdateUserPreferences, {
        onError: () => {
          if (lastSyncedRef.current === value) {
            lastSyncedRef.current = null;
            hydratedRef.current = false;
            forceRetry();
          }
        },
      });
      return;
    }
    lastSyncedRef.current = stored;
    if (stored !== value) {
      pendingHydrationRef.current = value;
      setValue(stored as NonNullable<UserPreferences[K]>);
    }
  }, [
    enabled,
    key,
    mutate,
    preferences.data,
    preferences.isSuccess,
    retryTick,
    setValue,
    userId,
    value,
  ]);

  useEffect(() => {
    if (!enabled || !hydratedRef.current || !userId) return;
    if (pendingHydrationRef.current !== null) {
      if (value === pendingHydrationRef.current) return;
      pendingHydrationRef.current = null;
    }
    if (value === lastSyncedRef.current) return;
    lastSyncedRef.current = value;
    mutate({ [key]: value } as UpdateUserPreferences, {
      onError: () => {
        if (lastSyncedRef.current === value) lastSyncedRef.current = null;
      },
    });
  }, [enabled, key, mutate, userId, value]);
}
